import { create } from 'zustand'

import {
  getAgentConfigs,
  getAgentRuns,
  approveAgentRun,
  rejectAgentRun,
  getAgentStatus,
  updateAgentConfig,
  type AgentConfig,
  type AgentRun,
} from '../api/agentService'

export interface AgentState {
  configs: AgentConfig[]
  runs: AgentRun[]
  runsLoading: boolean
  runsError: string | null
  pendingCount: number
  isLoading: boolean
  error: string | null
  loadConfigs: () => Promise<void>
  loadRuns: (params?: { status?: string; agentId?: string }) => Promise<void>
  loadRunsByAgent: (agentId: string) => Promise<void>
  loadStatus: () => Promise<void>
  approveRun: (id: string) => Promise<void>
  rejectRun: (id: string) => Promise<void>
  updateConfig: (agentId: string, data: Partial<AgentConfig>) => Promise<void>
}

export const useAgentStore = create<AgentState>((set) => ({
  configs: [],
  runs: [],
  runsLoading: false,
  runsError: null,
  pendingCount: 0,
  isLoading: false,
  error: null,

  loadConfigs: async () => {
    set({ isLoading: true, error: null })
    try {
      const configs = await getAgentConfigs()
      set({ configs, isLoading: false })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error loading configs', isLoading: false })
    }
  },

  loadRuns: async (params) => {
    set({ runsLoading: true, runsError: null })
    try {
      const runs = await getAgentRuns(params)
      set({ runs, runsLoading: false })
    } catch (err) {
      set({ runsError: err instanceof Error ? err.message : 'Error loading runs', runsLoading: false })
    }
  },

  loadRunsByAgent: async (agentId) => {
    set({ runsLoading: true, runsError: null })
    try {
      const runs = await getAgentRuns({ agentId })
      set({ runs, runsLoading: false })
    } catch (err) {
      set({ runsError: err instanceof Error ? err.message : 'Error loading runs', runsLoading: false })
    }
  },

  loadStatus: async () => {
    try {
      const status = await getAgentStatus()
      set({ pendingCount: status.pendingApprovals })
    } catch {
      // silently fail
    }
  },

  approveRun: async (id) => {
    try {
      await approveAgentRun(id)
      set((s) => ({
        runs: s.runs.map((r) => (r.id === id ? { ...r, status: 'approved' as const } : r)),
        pendingCount: Math.max(0, s.pendingCount - 1),
      }))
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to approve run',
      })
      throw err
    }
  },

  rejectRun: async (id) => {
    try {
      await rejectAgentRun(id)
      set((s) => ({
        runs: s.runs.map((r) => (r.id === id ? { ...r, status: 'rejected' as const } : r)),
        pendingCount: Math.max(0, s.pendingCount - 1),
      }))
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to reject run',
      })
      throw err
    }
  },

  updateConfig: async (agentId, data) => {
    const updated = await updateAgentConfig(agentId, data)
    set((s) => ({
      configs: s.configs.map((c) => (c.agentId === agentId ? { ...c, ...updated } : c)),
    }))
  },
}))
